import { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Header, MobileDrawer } from './components/Header/Header';
import { AppShell } from './components/Layout/AppShell';
import { AuthModal, type AuthMode } from './components/Modal/AuthModal';
import { MobileBottomNav } from './components/MobileBottomNav/MobileBottomNav';
import type { BetSelection } from './types/domain';

const SportsPage = lazy(() => import('./pages/SportsPage').then((module) => ({ default: module.SportsPage })));
const CasinoPage = lazy(() => import('./pages/CasinoPage').then((module) => ({ default: module.CasinoPage })));
const PromotionsPage = lazy(() => import('./pages/PromotionsPage').then((module) => ({ default: module.PromotionsPage })));
const ResultsPage = lazy(() => import('./pages/InfoPage').then((module) => ({ default: module.ResultsPage })));
const StaticInfoPage = lazy(() => import('./pages/InfoPage').then((module) => ({ default: module.StaticInfoPage })));

export function App() {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>(null);
  const [selections, setSelections] = useState<BetSelection[]>([]);
  const sportsLayout = useMemo(() => ['/', '/sports', '/live', '/cricket', '/esports'].includes(location.pathname), [location.pathname]);

  const toggleOdd = useCallback((selection: BetSelection) => {
    setSelections((current) => current.some((item) => item.oddId === selection.oddId) ? current.filter((item) => item.oddId !== selection.oddId) : [...current, selection]);
  }, []);
  const removeOdd = useCallback((oddId: string) => setSelections((current) => current.filter((selection) => selection.oddId !== oddId)), []);
  const clearBets = useCallback(() => setSelections([]), []);
  const closeModal = useCallback(() => setAuthMode(null), []);

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <Header menuOpen={drawerOpen} onMenuToggle={() => setDrawerOpen((value) => !value)} onLogin={() => setAuthMode('login')} onRegister={() => setAuthMode('register')} />
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <AppShell sportsLayout={sportsLayout} selections={selections} onRemove={removeOdd} onClear={clearBets} onRegister={() => setAuthMode('register')}>
        <Suspense fallback={<div className="page-loader" role="status"><span />Loading interface…</div>}>
          <Routes>
            <Route path="/" element={<SportsPage title="Top events" subtitle="Most watched today" selections={selections} onToggleOdd={toggleOdd} />} />
            <Route path="/sports" element={<SportsPage title="Sports" subtitle="Pre-match schedule" selections={selections} onToggleOdd={toggleOdd} />} />
            <Route path="/live" element={<SportsPage title="Live" subtitle="Events in progress" liveOnly selections={selections} onToggleOdd={toggleOdd} />} />
            <Route path="/cricket" element={<SportsPage title="Cricket" subtitle="Featured cricket" initialSport="cricket" selections={selections} onToggleOdd={toggleOdd} />} />
            <Route path="/esports" element={<SportsPage title="Esports" subtitle="Competitive gaming" initialSport="esports" selections={selections} onToggleOdd={toggleOdd} />} />
            <Route path="/casino" element={<CasinoPage />} />
            <Route path="/live-casino" element={<CasinoPage live />} />
            <Route path="/fast-games" element={<CasinoPage fast />} />
            <Route path="/promotions" element={<PromotionsPage />} />
            <Route path="/results" element={<ResultsPage />} />
            <Route path="/statistics" element={<ResultsPage statistics />} />
            <Route path="/information/*" element={<StaticInfoPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </AppShell>
      <MobileBottomNav selectionCount={selections.length} />
      <AuthModal mode={authMode} onClose={closeModal} onModeChange={setAuthMode} />
    </>
  );
}
